import { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import HeaderUnified from '../Header/HeaderUnified';
import PromotionsBanner from './PromotionsBanner';
import PromoSection from './PromoSection';
import HomeSection from './HomeSection';
import HomeProductCard from './HomeProductCard';
import ProductGrid from '../Business/ProductGrid';
import FilterPills from './FilterPills';
import FiltersModal from './FiltersModal';
import RangeFilterSheet from './RangeFilterSheet';
import PushPermissionModal from '../PushPermissionModal';
import ProductDetailSheet from '../Business/ProductDetailSheet';
import SearchOverlay from '../Search/SearchOverlay';
import CategoriesModal from './CategoriesModal';
import MenuFlotante from './MenuFlotante';
import TituloSeccion from './TituloSeccion';
import { estaDisponible } from '../../utils/disponibilidad';
import { useEntradaHome } from '../../hooks/useEntradaHome';

// View-only: recibe state/handlers desde MarketSectionPage. No fetchea, no calcula.
// Single-restaurant: header + banner + chips de categoría (menú) + productos.
export default function MarketSectionView({
  menuCategories = [],
  menuLoading = false,
  menuError = null,
  popularProducts = [],
  popularSection = {},
  promoSection = null,
  menuButtonImage = null,
  unavailableTagImage = null,
  menuAddButtonImage = null,
  footerImage = null,
  filterIcons = null,
  filtersUI,
  onMenuProductClick,
  sticky,
  activeCategory,
  sectionRefs,
  selectedProduct,
  searchOverlayOpen,
  showPushModal,
  onOpenLocationModal,
  onSearchTap,
  onCloseSearchOverlay,
  onCategoryClick,
  onCloseProductDetail,
  onAddToCartFromDetail,
  onPushRequest,
  onPushDismiss,
  cartItemsCount,
  scrollContainerRef,
  isActive = true,
}) {
  // Modal "ver todas las categorías" (UI presentacional, estado local).
  const [allCategoriesOpen, setAllCategoriesOpen] = useState(false);
  // Entrada en cascada al quitarse el splash (ver useEntradaHome).
  const entrada = useEntradaHome();

  const {
    categoriesStuck,
    filtersStuck,
    searchWhite,
    filtersHidden,
    fixedHeaderHeight,
    categoriesBarHeight,
    showBackToTop,
    cartBarVisible,
    headerCollapse,
    headerStuck,
    refs: { headerRef, searchRowRef, categoriesSentinelRef, filtersSentinelRef, categoriesBarRef },
  } = sticky;

  // El buscador queda montado entre el header y la sección de abajo: el fondo
  // del header llega hasta su mitad y la sección de abajo sube esa misma mitad
  // por detrás. La fila del buscador tiene el mismo aire arriba y abajo, así
  // que su mitad es la mitad de la caja blanca.
  const mitadBuscador = Math.round(fixedHeaderHeight / 2);

  // Offsets de los dos stickys: cada uno arranca donde termina el de arriba.
  const categoriesTop = fixedHeaderHeight - 1;
  // -1 extra: el alto medido redondea hacia arriba y dejaba una rendija de ~1px
  // entre ambas barras. Solapar es inocuo (las categorías van por encima).
  const filtersTop = categoriesTop + categoriesBarHeight - 1;

  // La barra de categorías (y su "Ver todos") solo lleva las marcadas para
  // salir ahí; "Disponibles todo el día" vive únicamente al inicio del menú.
  const categoriasBarra = menuCategories.filter((c) => c.showInCategoryBar);

  return (
    <div className="min-h-full bg-white pb-0">
      {/* Header sticky colapsable: se pega con top negativo, así la fila de la
          ubicación se desliza fuera y el MISMO buscador queda anclado arriba.
          Antes había una copia flotante en un portal que entraba desde el borde
          superior — se veía como un segundo buscador apareciendo. */}
      <HeaderUnified
        headerRef={headerRef}
        searchRowRef={searchRowRef}
        stickyOffset={headerCollapse}
        searchWhite={searchWhite}
        onOpenLocationModal={onOpenLocationModal}
        onSearchTap={onSearchTap}
        whiteMode={false}
        mitadBuscador={mitadBuscador}
        anclado={headerStuck}
      />

      {/* Zona promocional debajo del header. Si la sección por bloques está
          encendida releva al banner de siempre; si no, se pinta el de siempre.
          Sube `mitadBuscador` por detrás del buscador (margen negativo) y
          compensa con el mismo relleno arriba: el fondo de la sección llega
          hasta la mitad del buscador sin mover su contenido. */}
      {/* Sin fondo propio: cada hijo pone el suyo. La promo de abajo de la
          sección se monta mitad sobre la sección y mitad sobre el blanco, y
          un fondo aquí la taparía. */}
      <div
        className="overflow-hidden relative z-10"
        style={{ marginTop: -mitadBuscador }}
      >
        {promoSection?.isActive ? (
          <PromoSection
            config={promoSection}
            todosLosProductos={menuCategories.flatMap((c) => c.products || [])}
            onProductClick={onMenuProductClick}
            rellenoSuperior={mitadBuscador}
          />
        ) : (
          <div className="bg-promo-section-bg" style={{ paddingTop: mitadBuscador }}>
            <div {...entrada(0)}>
              <PromotionsBanner />
            </div>
          </div>
        )}
      </div>

      <div ref={categoriesSentinelRef} className="h-0 w-full" aria-hidden="true" />

      {/* Chips de categorías del menú (sticky) */}
      <div
        ref={categoriesBarRef}
        className={`sticky z-30 bg-white pb-0 pt-0 transition-shadow duration-200 ease-out ${
          // La sombra va en los filtros mientras se ven (son un solo bloque).
          // Solo cuando los filtros se esconden, las categorías la recogen para
          // seguir separándose del contenido.
          categoriesStuck && !(filtersStuck && !filtersHidden) ? 'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]' : ''
        }`}
        style={{ top: `${categoriesTop}px` }}
      >
        {/* Relleno fijo a propósito: antes bajaba de pt-2.5 a pt-2 al pegarse,
            con transición, y como los filtros se pegan justo debajo de esta
            barra, se recolocaban cuadro por cuadro ("se movían"). La
            compactación al pegarse la hacen las fotos con scale. */}
        <div className="overflow-x-auto scrollbar-hide px-4 pb-0 pt-2.5">
          {/* w-max → el ancho de la fila es el de los chips, así la línea divisoria
              va de la primera categoría a la última y no de borde a borde de la
              pantalla. Va aquí adentro (no en el contenedor sticky) para que la
              barra verde de cada chip caiga justo sobre ella. */}
          <div className="flex gap-3 w-max border-b border-gray-100">
          {categoriasBarra.map((category, i) => {
            const active = activeCategory === category._id;
            return (
              <button
                key={category._id}
                data-category-id={category._id}
                onClick={() => onCategoryClick(category._id)}
                // Entran una tras otra después de la promo (solo las que caben
                // en pantalla llevan retraso de más).
                {...entrada(220 + Math.min(i, 5) * 40, 'arriba', 'relative flex flex-col items-center flex-shrink-0 w-16 pb-2')}
              >
                {/* Imagen sola (misma en activa y no activa). El estado activo lo
                    marcan el color del nombre y la barra inferior. */}
                <div className={`flex items-center justify-center h-11 transition-transform duration-200 ease-out ${categoriesStuck ? 'scale-90' : 'scale-100'}`}>
                  {category.image ? (
                    <img src={category.image} alt={category.name} className="w-16 h-11 object-contain object-bottom" loading="lazy" />
                  ) : (
                    <span className="text-3xl">{category.icon || '🍽️'}</span>
                  )}
                </div>
                {/* Sin margen: la caja de la imagen ya está ajustada a su alto real
                    (h-11 ≈ 64/1.5), así que el nombre arranca donde termina la foto. */}
                <span className={`max-w-full truncate text-[13px] tracking-tight text-center transition-colors duration-200 ${
                  active ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                }`}>
                  {category.name}
                </span>
                {/* Barra de la categoría activa: hija del botón, así abarca todo el
                    chip como en la referencia. -bottom-px la baja 1px para montarse
                    sobre la línea divisoria. */}
                <span
                  aria-hidden="true"
                  className={`absolute -bottom-px left-0 right-0 h-[3px] rounded-t-full bg-primary-500 transition-all duration-200 ease-out ${
                    active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                  }`}
                />
              </button>
            );
          })}

          {/* Ver todos — abre el modal con todas las categorías de la barra */}
          {categoriasBarra.length > 0 && (
            <button
              type="button"
              onClick={() => setAllCategoriesOpen(true)}
              className="flex flex-col items-center flex-shrink-0 w-16 pb-2"
              aria-label="Ver todas las categorías"
            >
              <div className={`flex items-center justify-center h-11 transition-transform duration-200 ease-out ${categoriesStuck ? 'scale-90' : 'scale-100'}`}>
                <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg viewBox="0 0 556.92 556.92" width="20" height="20" className="text-primary-600" aria-hidden="true">
                    <path fill="currentColor" d="M76.285,76.093c-7.95,22.114-16.87,44.342-19.657,67.849c-2.623,22.09,2.515,38.155,13.333,57.078c10.841,18.969,29.856,46.778,52.675,59.536V556.92h51.494V260.865c23.069-12.61,42.313-40.716,53.244-59.841c10.817-18.923,15.952-34.988,13.333-57.079c-2.791-23.507-11.708-45.734-19.657-67.849c-6.258-17.408-18.733-60.665-41.769-63.348l13.73,127.082h-17.164L162.109,12.24h-13.445h-13.446l-13.73,127.586h-17.164l13.73-127.082C95.019,15.429,82.546,58.685,76.285,76.093z" />
                    <path fill="currentColor" d="M426.576,556.92V265.302c42.812-13.629,74.392-60.79,74.392-116.892C500.967,81.656,456.263,0,401.116,0s-99.851,81.656-99.851,148.41c0,55.848,31.301,102.828,73.819,116.693V556.92H426.576z" />
                  </svg>
                </div>
              </div>
              <span className={`text-[13px] tracking-tight font-medium text-center text-primary-600 flex items-center justify-center gap-0.5 whitespace-nowrap transition-all duration-200 ease-out ${
                categoriesStuck ? 'mt-0' : 'mt-0'
              }`}>
                Ver todos
                <CaretDown size={11} weight="bold" />
              </span>
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Sección Populares — fondo + título (imagen/Lottie) + productos populares */}
      {popularProducts.length > 0 && (
        <HomeSection
          backgroundImage={popularSection.background}
          titleUrl={popularSection.title}
          titleType={popularSection.titleType}
        >
          <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-1">
            {popularProducts.map((product) => (
              <HomeProductCard
                key={product._id || product.id}
                product={product}
                addButtonImage={popularSection.addButtonImage}
                onClick={() => onMenuProductClick(product)}
              />
            ))}
          </div>
        </HomeSection>
      )}

      {/* "Explora nuestro menú": gris claro, mayúsculas espaciadas y ligeras,
          centrado sobre los filtros, como en la referencia. Va fuera del
          sticky: se queda arriba de los filtros y se va con el scroll. */}
      {/* pt-2: el pie de Tendencias ya trae 20px de aire; con pt-7 quedaba un
          hueco de casi 50px. */}
      <p {...entrada(380, 'arriba', 'pt-2 text-center text-[10px] font-light uppercase tracking-[0.25em] text-menu-explore')}>
        Explora nuestro menú
      </p>

      {/* Filtros del menú — van DEBAJO de la sección de tendencias, con su
          propio sentinel. Se pegan bajo las categorías cuando el scroll llega
          aquí; el div interno es el que se esconde al bajar y reaparece al
          subir, deslizándose por detrás de las categorías (z menor). */}
      <div ref={filtersSentinelRef} className="h-0 w-full" aria-hidden="true" />
      <div className="sticky z-20" style={{ top: `${filtersTop}px` }}>
        <div
          className={`bg-white transition-transform duration-200 ease-out ${
            filtersHidden ? '-translate-y-full' : 'translate-y-0'
          } ${
            filtersStuck && !filtersHidden ? 'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]' : ''
          }`}
        >
          <div {...entrada(400)}>
          <FilterPills
            filters={filtersUI.filters}
            iconos={filterIcons}
            onSetValue={filtersUI.setFilterValue}
            onOpenRangeSheet={filtersUI.openRangeSheet}
            onOpenModal={filtersUI.openFiltersModal}
          />
          </div>
        </div>
      </div>

      {/* Menú: productos agrupados por categoría */}
      <div {...entrada(420, 'arriba', 'px-3 py-3')}>
        {menuLoading ? (
          // Siluetas con la forma de las tarjetas del menú en vez de un spinner
          // solo: si la red tarda más que el splash, la pantalla ya se ve armada.
          // Solo pasa en la primera apertura; después hay copia guardada.
          <div aria-hidden="true" className="animate-pulse">
            <div className="h-5 w-48 mx-1 mb-4 rounded bg-gray-100" />
            <div className="grid grid-cols-2 gap-x-2.5 gap-y-6">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="aspect-[6/5] rounded-2xl bg-gray-100" />
                  <div className="h-4 w-14 mt-2.5 rounded bg-gray-100" />
                  <div className="h-4 w-28 mt-1.5 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          </div>
        ) : menuError ? (
          <div className="text-center py-10 text-gray-500">Error al cargar el menú</div>
        ) : menuCategories.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            {filtersUI.filtersActive
              ? 'Ningún platillo coincide con esos filtros'
              : 'Este restaurante aún no tiene productos en su menú'}
          </div>
        ) : (
          <div className="space-y-9">
            {menuCategories.map((category) => (
              <section
                key={category._id}
                ref={(el) => (sectionRefs.current[category._id] = el)}
                className="scroll-mt-52"
              >
                <TituloSeccion className="px-1 mb-4">{category.name}</TituloSeccion>
                <ProductGrid
                  products={category.products}
                  onProductClick={onMenuProductClick}
                  unavailableTagImage={unavailableTagImage}
                  addButtonImage={menuAddButtonImage}
                />
              </section>
            ))}
          </div>
        )}
      </div>

      {showBackToTop && (
        <button
          onClick={() => {
            const container = scrollContainerRef?.current;
            if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
            else window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className={`fixed left-1/2 -translate-x-1/2 z-30 bg-black text-white px-3 py-2.5 rounded-full shadow-md hover:opacity-90 transition-[bottom] duration-300 ease-out animate-fade-in flex items-center gap-1.5 ${
            cartItemsCount > 0 && cartBarVisible ? 'bottom-[84px]' : 'bottom-6'
          }`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m18 15-6-6-6 6"/>
          </svg>
          <span className="text-[13px] font-medium">Volver arriba</span>
        </button>
      )}

      {showPushModal && (
        <PushPermissionModal onRequest={onPushRequest} onDismiss={onPushDismiss} />
      )}

      {/* SearchOverlay: solo la sección ACTIVA lo renderiza. */}
      {isActive && (
        <SearchOverlay
          isOpen={searchOverlayOpen}
          onClose={onCloseSearchOverlay}
          onProductClick={onMenuProductClick}
        />
      )}

      <CategoriesModal
        isOpen={allCategoriesOpen}
        onClose={() => setAllCategoriesOpen(false)}
        categories={categoriasBarra}
        onSelect={onCategoryClick}
      />

      <FiltersModal
        isOpen={filtersUI.filtersModalOpen}
        onClose={filtersUI.closeFiltersModal}
        filters={filtersUI.filters}
        onApply={filtersUI.setFilters}
      />

      <RangeFilterSheet
        isOpen={!!filtersUI.activeRangeSheet}
        onClose={filtersUI.closeRangeSheet}
        title={filtersUI.activeRangeSheet?.title || ''}
        options={filtersUI.activeRangeSheet?.options || []}
        currentValue={filtersUI.rangeSheetKey ? filtersUI.filters[filtersUI.rangeSheetKey] : null}
        onApply={(value) => filtersUI.setFilterValue(filtersUI.rangeSheetKey, value)}
      />

      <ProductDetailSheet
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={onCloseProductDetail}
        onAddToCart={onAddToCartFromDetail}
        isBusinessOpen={estaDisponible(selectedProduct)}
        disabledLabel="No disponible"
      />

      {/* Botón flotante "Menú": solo la sección ACTIVA lo pinta (va en portal,
          así que la inactiva lo dejaría encimado). HomeCartSummary vive en
          MarketLayout. */}
      {isActive && (
        <MenuFlotante
          categorias={menuCategories}
          activa={activeCategory}
          onSelect={onCategoryClick}
          imagen={menuButtonImage}
          subir={cartItemsCount > 0 && cartBarVisible}
        />
      )}

      {/* Imagen del pie del home (dashboard → Home). Sin una subida, la
          empaquetada de siempre. */}
      <img
        src={footerImage || '/assets/footer-banner.png'}
        alt=""
        {...(!footerImage && { width: 1388, height: 1368 })}
        className="w-full h-auto object-cover mt-8"
        loading="lazy"
      />
    </div>
  );
}
